/**
 * Marker icons for the app's own annotations, drawn as raw pixels.
 *
 * MapLibre needs an image for `icon-image`, and this style ships no sprite
 * sheet. Rather than add one — a build step, a file to keep in step with the
 * palettes, and a network fetch that would fail offline exactly when the chart
 * matters — the icons are generated in a few lines of arithmetic at runtime and
 * handed to `map.addImage`. They cost nothing, they cannot 404, and they
 * re-colour for the night palette for free.
 *
 * **Deliberately not chart symbols.** S-52 has a defined symbol vocabulary and
 * neither a webcam nor a marina listing is in it. Both icons therefore sit in
 * the same visual family — a filled marker body with a contrasting halo, in
 * colours outside the chart's palette — so they read as app annotations rather
 * than as charted objects. That matters most for the marina anchor, which could
 * otherwise be taken for the S-52 anchorage symbol: that one is a bare magenta
 * anchor glyph with no container, and this one never appears without its body.
 */

export interface IconImage {
  width: number;
  height: number;
  data: Uint8ClampedArray;
}

export interface MarkerColours {
  /** Marker body. */
  body: string;
  /** Outline and cut-out detail — carries the icon against a dark chart. */
  halo: string;
}

/** #rrggbb to [r, g, b]. */
function rgb(hex: string): [number, number, number] {
  const value = hex.replace('#', '');
  const full = value.length === 3
    ? value.split('').map((c) => c + c).join('')
    : value;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

/** Signed distance to a rounded rectangle: negative inside, positive outside. */
function roundedRectDistance(
  x: number, y: number,
  left: number, top: number, right: number, bottom: number,
  radius: number,
): number {
  const halfWidth = (right - left) / 2 - radius;
  const halfHeight = (bottom - top) / 2 - radius;
  const dx = Math.abs(x - (left + right) / 2) - halfWidth;
  const dy = Math.abs(y - (top + bottom) / 2) - halfHeight;
  const outside = Math.hypot(Math.max(dx, 0), Math.max(dy, 0));
  return outside + Math.min(Math.max(dx, dy), 0) - radius;
}

/**
 * A camera: body, lens, and a small viewfinder hump so the silhouette reads at
 * marker size rather than turning into a blob.
 *
 * Drawn at `pixelRatio` 2 and declared as such, so it stays crisp on a phone.
 * Anti-aliasing is a one-pixel linear ramp on each signed distance — enough to
 * stop the diagonal edges looking chewed, and cheap.
 */
export function webcamIcon(colours: MarkerColours, size = 40): IconImage {
  const data = new Uint8ClampedArray(size * size * 4);
  const [bodyR, bodyG, bodyB] = rgb(colours.body);
  const [haloR, haloG, haloB] = rgb(colours.halo);

  const s = size / 40; // The geometry below is authored against a 40px tile.
  const lensX = 20 * s;
  const lensY = 23 * s;
  const lensOuter = 7.5 * s;
  const lensInner = 4.5 * s;

  const coverage = (distance: number): number => Math.min(1, Math.max(0, 0.5 - distance));

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const px = x + 0.5;
      const py = y + 0.5;

      // Body, plus the viewfinder hump sitting on top of it.
      const body = Math.min(
        roundedRectDistance(px, py, 4 * s, 13 * s, 36 * s, 34 * s, 4 * s),
        roundedRectDistance(px, py, 13 * s, 6 * s, 25 * s, 15 * s, 2 * s),
      );

      const bodyCoverage = coverage(body);
      const haloCoverage = coverage(body - 2.5 * s) - bodyCoverage;

      const lensDistance = Math.hypot(px - lensX, py - lensY);
      const lensRing = coverage(lensDistance - lensOuter);
      const lensHole = coverage(lensDistance - lensInner);

      let r = 0; let g = 0; let b = 0; let a = 0;

      // Painter's order: halo, body, lens ring, lens centre.
      const mix = (cr: number, cg: number, cb: number, ca: number): void => {
        if (ca <= 0) return;
        r = cr * ca + r * (1 - ca);
        g = cg * ca + g * (1 - ca);
        b = cb * ca + b * (1 - ca);
        a = ca + a * (1 - ca);
      };

      mix(haloR, haloG, haloB, haloCoverage);
      mix(bodyR, bodyG, bodyB, bodyCoverage);
      mix(haloR, haloG, haloB, Math.min(lensRing, bodyCoverage));
      mix(bodyR, bodyG, bodyB, Math.min(lensHole, bodyCoverage));

      const offset = (y * size + x) * 4;
      data[offset] = r;
      data[offset + 1] = g;
      data[offset + 2] = b;
      data[offset + 3] = a * 255;
    }
  }

  return { width: size, height: size, data };
}

/**
 * The boat itself: a hull seen from above, bow at the top of the tile.
 *
 * Drawn pointing north so `icon-rotate` can be handed the course over ground
 * directly — MapLibre rotates clockwise from up, which is what a bearing already
 * is. The caller is responsible for only using it when there IS a course: a
 * symbol with a bow points somewhere, and pointing it in a direction the GPS
 * invented would be a lie told sixty times a minute. Without one the chart falls
 * back to the plain circle, which claims nothing.
 *
 * The shape is deliberately not an arrowhead. An arrow reads as "direction of
 * travel" and nothing else; a hull with a pointed bow, straight sides and a
 * square transom also says which end is which when the boat is stopped in the
 * water and being set sideways by a current — the case where an arrow would be
 * actively misleading.
 */
export function vesselIcon(colours: MarkerColours, size = 40): IconImage {
  const data = new Uint8ClampedArray(size * size * 4);
  const [bodyR, bodyG, bodyB] = rgb(colours.body);
  const [haloR, haloG, haloB] = rgb(colours.halo);

  const s = size / 40; // Authored against a 40px tile.
  const cx = 20 * s;
  const bow = 5 * s;
  const stern = 34 * s;
  const beam = 7.5 * s;
  // Where the hull reaches full beam. Forward of this the sides sweep in to the
  // stem; aft of it they stay parallel to a square transom.
  const shoulder = 17 * s;

  const coverage = (distance: number): number => Math.min(1, Math.max(0, 0.5 - distance));

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const px = x + 0.5;
      const py = y + 0.5;

      // Half-width of the hull at this station: an ogive forward, parallel aft.
      const halfWidth = py < shoulder
        ? beam * Math.sqrt(Math.max(0, (py - bow) / (shoulder - bow)))
        : beam;
      // Signed distance: positive outside the hull, and the ends closed off.
      const hull = Math.max(
        Math.abs(px - cx) - halfWidth,
        bow - py,
        py - stern,
      );

      const bodyCoverage = coverage(hull);
      const haloCoverage = coverage(hull - 2 * s) - bodyCoverage;

      let r = 0; let g = 0; let b = 0; let a = 0;
      const mix = (cr: number, cg: number, cb: number, ca: number): void => {
        if (ca <= 0) return;
        r = cr * ca + r * (1 - ca);
        g = cg * ca + g * (1 - ca);
        b = cb * ca + b * (1 - ca);
        a = ca + a * (1 - ca);
      };

      mix(haloR, haloG, haloB, haloCoverage);
      mix(bodyR, bodyG, bodyB, bodyCoverage);

      const offset = (y * size + x) * 4;
      data[offset] = r;
      data[offset + 1] = g;
      data[offset + 2] = b;
      data[offset + 3] = a * 255;
    }
  }

  return { width: size, height: size, data };
}

/** Signed distance to a line segment. */
function segmentDistance(
  x: number, y: number,
  x1: number, y1: number, x2: number, y2: number,
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lengthSquared = dx * dx + dy * dy;
  const t = lengthSquared === 0
    ? 0
    : Math.min(1, Math.max(0, ((x - x1) * dx + (y - y1) * dy) / lengthSquared));
  return Math.hypot(x - (x1 + t * dx), y - (y1 + t * dy));
}

/**
 * A marina: an anchor inside a map pin.
 *
 * The pin shape is what keeps this away from S-52. A bare anchor is the
 * chart's own anchorage symbol, so this one is always inside a teardrop body
 * and in a colour the chart does not use — it reads as "something the app put
 * here", which is exactly what it is. The anchor inside is what makes it
 * legible at a glance as a marina rather than a generic pin.
 */
export function harbourIcon(colours: MarkerColours, size = 40): IconImage {
  const data = new Uint8ClampedArray(size * size * 4);
  const [bodyR, bodyG, bodyB] = rgb(colours.body);
  const [haloR, haloG, haloB] = rgb(colours.halo);

  const s = size / 40; // Authored against a 40px tile.
  const cx = 20 * s;
  const cy = 17 * s;
  const headRadius = 13 * s;

  const coverage = (distance: number): number => Math.min(1, Math.max(0, 0.5 - distance));

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const px = x + 0.5;
      const py = y + 0.5;

      // Pin: a circle with a tapering tail, so it points at its position
      // rather than sitting centred on it.
      const head = Math.hypot(px - cx, py - cy) - headRadius;
      const tailWidth = 8 * s * Math.max(0, 1 - (py - cy) / (20 * s));
      const tail = Math.max(
        Math.abs(px - cx) - tailWidth,
        Math.max(cy - py, py - 37 * s),
      );
      const pin = Math.min(head, tail);

      const bodyCoverage = coverage(pin);
      const haloCoverage = coverage(pin - 2.5 * s) - bodyCoverage;

      // Anchor: ring, shank, stock, and two flukes sweeping up from the crown.
      const ring = Math.abs(Math.hypot(px - cx, py - (11 * s)) - 2.6 * s) - 0.9 * s;
      const shank = segmentDistance(px, py, cx, 11 * s, cx, 23.5 * s) - 1.2 * s;
      const stock = segmentDistance(px, py, cx - 6 * s, 15 * s, cx + 6 * s, 15 * s) - 1.1 * s;
      // The curved arms: an arc centred above the crown, kept to the lower half.
      const armDistance = Math.abs(Math.hypot(px - cx, py - (16 * s)) - 7.5 * s) - 1.2 * s;
      const arms = Math.max(armDistance, (17.5 * s) - py);

      const anchor = Math.min(ring, shank, stock, arms);
      const anchorCoverage = Math.min(coverage(anchor), bodyCoverage);

      let r = 0; let g = 0; let b = 0; let a = 0;
      const mix = (cr: number, cg: number, cb: number, ca: number): void => {
        if (ca <= 0) return;
        r = cr * ca + r * (1 - ca);
        g = cg * ca + g * (1 - ca);
        b = cb * ca + b * (1 - ca);
        a = ca + a * (1 - ca);
      };

      mix(haloR, haloG, haloB, haloCoverage);
      mix(bodyR, bodyG, bodyB, bodyCoverage);
      mix(haloR, haloG, haloB, anchorCoverage);

      const offset = (y * size + x) * 4;
      data[offset] = r;
      data[offset + 1] = g;
      data[offset + 2] = b;
      data[offset + 3] = a * 255;
    }
  }

  return { width: size, height: size, data };
}
